import React, {
  CSSProperties,
  KeyboardEventHandler,
  useEffect,
  useRef,
} from 'react';
import styled from '@emotion/styled';
import { Path, useFormContext } from 'react-hook-form';
import SvgImage from '@/components/Module/SvgImage';
import { StyledButton } from '@/components/MobileSales/DrawerList/styles';
import { RecordFilter } from '@/types/api';
import usePlatform from '@/components/Record/hooks/usePlatform';

type P = {
  isFocus?: boolean;
  leftChildren?: React.ReactNode;
  rightChildren?: React.ReactNode;
  handleKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  haveClear?: boolean;
  registerName: Path<RecordFilter>;
  autoFocus?: boolean;
  style?: CSSProperties;
};

const AttenderInput = ({
  isFocus,
  leftChildren,
  rightChildren,
  handleKeyDown,
  onBlur,
  onFocus,
  placeholder,
  haveClear,
  registerName,
  autoFocus = true,
  style,
}: P) => {
  const { isMobilePath } = usePlatform();
  const { register, setValue } = useFormContext<RecordFilter>();
  const leftRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (leftRef.current) {
      leftRef.current.scrollLeft = leftRef.current.scrollWidth;
    }
  }, [leftChildren]);

  const onClear = () => {
    setValue(registerName, '');
  };

  return (
    <StyledWrapper
      isFocus={isFocus}
      style={{ ...style }}
      mobileMode={isMobilePath}
    >
      <div style={{ flexGrow: 1 }}>
        <div
          ref={leftRef}
          style={{
            display: 'flex',
            gap: '8px',
            marginRight: '8px',
            flexWrap: 'wrap',
          }}
        >
          {leftChildren}
          <StyledInput
            {...register(registerName)}
            type={'text'}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
          />
          {haveClear && (
            <StyledButton onClick={onClear}>
              <SvgImage name={'clear'} />
            </StyledButton>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', height: '30px', alignItems: 'center' }}>
        {rightChildren}
      </div>
    </StyledWrapper>
  );
};
export default AttenderInput;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  height: 28px;

  :placeholder-shown {
    text-overflow: ellipsis;
  }

  ::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
  }

  :focus {
    outline: none;
  }
`;

const StyledWrapper = styled.div<{ isFocus?: boolean; mobileMode?: boolean }>`
  display: flex;
  border-radius: 6px;
  border: 1px solid;
  padding: 10px 16px 10px 12px;
  align-items: ${({ mobileMode }) => (mobileMode ? 'center' : 'flex-start')};
  border-color: ${({ isFocus }) => (isFocus ? 'black' : 'var(--dk-line, #e7e9ec)')};
  &:focus-within {
    border-color: black;
  }
  box-shadow: ${({ isFocus }) =>
    isFocus ? '0 0 4px 0 rgba(0, 102, 204, 0.25)' : ''};
`;
